import React, { useReducer, useEffect } from 'react';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import OfferPartnerEditQuestionsListComponent from './OfferPartnerEditQuestionsListComponent';
import OfferPartnerQuestionsNewItemContainer from './OfferPartnerQuestionsNewItemContainer';
import { getOfferPartnerQuestions, getPartnerQuestionsIsLoaded, getTotalScore } from '../../selectors';
import useEventCallback from 'utils/hooks/useEventCallback';
import { loadOfferPartnerQuestions, addOrUpdateQuestionnaireId, startQuestion, nextQuestion, finishQuestion } from '../../actionCreators';
import { reducer, initialState } from '../../reducers/containerReducer';
import { Grid } from '@mui/material';

const OfferPartnerEditQuestionnaireContainer = ({
  documentId,
  siteId,
  partnerId,
  questionnaireId,
  partnerQuestions,
  totalScore,
  partnerQuestionsLoaded,
  history,
  onChangeActiveStep,
  actions
}) => {
  const [localState, dispatch] = useReducer(reducer, initialState);

  const onContinue = useEventCallback((type, data, callback = () => {}) => {
    switch (type) {
      case 'StartQuestion':
        return actions.startQuestion(data, callback);
      case 'NextQuestion':
        return actions.nextQuestion(data, callback);
      case 'FinishQuestion':
        return actions.finishQuestion(data, callback);
      default:
        return;
    }
  }, []);

  const onStartCallback = (startQuestionNo, nextQuestion) => {
    const answer = nextQuestion.answer || nextQuestion.answers.find(a => a.isDefault)?.questionValue;
    const questionScore = nextQuestion.answers.find(a => a.isDefault)?.questionScore;

    dispatch({
      type: 'OnStart',
      startQuestionNo: startQuestionNo,
      nextQuestion: {
        ...nextQuestion,
        answer,
        questionScore
      }
    });
  };

  const onStartQuestionnaire = questionNo => {
    if (questionNo <= 0) {
      onContinue(
        'StartQuestion',
        {
          documentId: localState.questionnaire.documentId,
          siteId: localState.questionnaire.siteId,
          partnerId: localState.questionnaire.partnerId,
          questionnaireId: localState.questionnaire.questionnaireId,
          startQuestionNo: 0
        },
        onStartCallback
      );
    } else {
      const newAnsweredQuestions = Object.assign([...localState.answeredQuestions]);
      newAnsweredQuestions.splice(questionNo - 1);
      onContinue(
        'NextQuestion',
        {
          documentId: localState.questionnaire.documentId,
          siteId: localState.questionnaire.siteId,
          partnerId: localState.questionnaire.partnerId,
          questionnaireId: localState.questionnaire.questionnaireId,
          answeredQuestions: newAnsweredQuestions,
          startQuestionNo: questionNo,
          currentQuestion: null
        },
        onStartCallback
      );
    }
  };

  const onNextCallback = (currentQuestion, nextQuestion) => {
    const answer = nextQuestion.answer || nextQuestion.answers.find(a => a.isDefault)?.questionValue;
    const questionScore = nextQuestion.answers.find(a => a.isDefault)?.questionScore;

    dispatch({
      type: 'OnNext',
      currentQuestion: currentQuestion,
      nextQuestion: {
        ...nextQuestion,
        answer,
        questionScore
      }
    });
  };

  const onNextQuestionnaire = () => {
    const newAnsweredQuestions = Object.assign([...localState.answeredQuestions]);
    if (localState.currentQuestion.loaded && !localState.currentQuestion.isEmptyQuestion)
      newAnsweredQuestions.push({ ...localState.currentQuestion, changed: true });

    onContinue(
      'NextQuestion',
      {
        documentId: localState.questionnaire.documentId,
        siteId: localState.questionnaire.siteId,
        partnerId: localState.questionnaire.partnerId,
        questionnaireId: localState.questionnaire.questionnaireId,
        answeredQuestions: newAnsweredQuestions,
        currentQuestion: localState.currentQuestion
      },
      onNextCallback
    );
  };

  const onRedirectCallback = (documentId, siteId, partnerId) => {
    onChangeActiveStep(-1);
    history.push(`/offers/${documentId}/${siteId}/partner/${partnerId}`);
  };

  const onSaveQuestionnaire = () => {
    const newAnsweredQuestions = Object.assign([...localState.answeredQuestions]);
    if (localState.currentQuestion.loaded && !localState.currentQuestion.isEmptyQuestion)
      newAnsweredQuestions.push({ ...localState.currentQuestion, changed: true });

    onContinue(
      'FinishQuestion',
      {
        documentId: localState.questionnaire.documentId,
        siteId: localState.questionnaire.siteId,
        partnerId: localState.questionnaire.partnerId,
        questionnaireId: localState.questionnaire.questionnaireId,
        answeredQuestions: newAnsweredQuestions
      },
      onRedirectCallback
    );
  };

  const onFinishQuestionnaire = () => {
    onNextQuestionnaire();
    dispatch({ type: 'OnFinish' });
  };

  const onDoneQuestionnaire = () => {
    onContinue(
      'FinishQuestion',
      {
        documentId: localState.questionnaire.documentId,
        siteId: localState.questionnaire.siteId,
        partnerId: localState.questionnaire.partnerId,
        questionnaireId: localState.questionnaire.questionnaireId,
        answeredQuestions: localState.answeredQuestions
      },
      onRedirectCallback
    );
  };

  const onCancelQuestionnaire = () => {
    onChangeActiveStep(-1);
    history.push(`/offers/${documentId}/${siteId}/partner/${partnerId}`);
  };

  const onQuestionnaireIdCallback = (questionnaireId, questionnaireName) => {
    dispatch({
      type: 'OnQuestionnaireUpdated',
      questionnaireId: questionnaireId,
      questionnaireName: questionnaireName
    });
  };

  useEffect(() => {
    if (!partnerQuestionsLoaded) {
      actions.loadOfferPartnerQuestions(documentId, siteId, partnerId);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (partnerQuestionsLoaded) {
      dispatch({ type: 'OnInitState', answeredQuestions: partnerQuestions, documentId, siteId, partnerId, totalScore });
      if (!questionnaireId) {
        actions.addOrUpdateQuestionnaireId({ documentId, siteId, partnerId, questionnaireId }, onQuestionnaireIdCallback);
      }
    }
  }, [partnerQuestionsLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  return partnerQuestionsLoaded ? (
    <Grid container gap={2} flexDirection='column'>
      {!localState.currentQuestion.isEmptyQuestion && (
        <OfferPartnerQuestionsNewItemContainer
          currentQuestion={localState.currentQuestion}
          partnerQuestions={localState.answeredQuestions}
          questionnaire={localState.questionnaire}
          dispatch={dispatch}
          onStartQuestionnaire={onStartQuestionnaire}
          onNextQuestionnaire={onNextQuestionnaire}
          onFinishQuestionnaire={onFinishQuestionnaire}
          onSaveQuestionnaire={onSaveQuestionnaire}
          onCancelQuestionnaire={onCancelQuestionnaire}
        />
      )}
      <OfferPartnerEditQuestionsListComponent
        partnerQuestions={localState.answeredQuestions}
        questionnaire={localState.questionnaire}
        editQuestionnaireMode={true}
        onStartQuestionnaireSpecificQuestion={onStartQuestionnaire}
        onDoneQuestionnaire={onDoneQuestionnaire}
        onCancelQuestionnaire={onCancelQuestionnaire}
        currentQuestionIsEmpty={localState.currentQuestion.isEmptyQuestion}
        isFinished={localState.isFinished}
      />
    </Grid>
  ) : (
    <></>
  );
};

OfferPartnerEditQuestionnaireContainer.propTypes = {
  documentId: PropTypes.number,
  siteId: PropTypes.number,
  partnerId: PropTypes.number,
  questionnaireId: PropTypes.number,
  actions: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
  partnerQuestions: PropTypes.array.isRequired,
  totalScore: PropTypes.number,
  partnerQuestionsLoaded: PropTypes.bool.isRequired,
  onChangeActiveStep: PropTypes.func.isRequired
};

function mapStateToProps(state, ownProps) {
  const partnerQuestions = getOfferPartnerQuestions(state, ownProps.documentId, ownProps.siteId, ownProps.partnerId);
  const totalScore = getTotalScore(state, ownProps.documentId, ownProps.siteId, ownProps.partnerId);
  const partnerQuestionsLoaded = getPartnerQuestionsIsLoaded(state, ownProps.documentId, ownProps.siteId, ownProps.partnerId);
  const partnerQuestionsLoading = !partnerQuestionsLoaded;

  return {
    partnerQuestions,
    totalScore,
    partnerQuestionsLoaded,
    partnerQuestionsLoading
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      { loadOfferPartnerQuestions, addOrUpdateQuestionnaireId, startQuestion, nextQuestion, finishQuestion },
      dispatch
    ),
    dispatch
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(OfferPartnerEditQuestionnaireContainer);
